import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import type { ChoirBrief, ClosedSession, CurrentSession, ScheduledSession } from '@contracts/index';

import { api } from '../api';
import { useBackKey } from '../backKey';
import { useHome } from '../home';
import { ChevronRightIcon } from '../icons';
import AheadGathering from './AheadGathering';
import BirthdayCard from './BirthdayCard';
import MarkScreen from './MarkScreen';
import { PastGathering } from './PastGathering';
import ProgramScreen from './ProgramScreen';
import Push from './Push';
import RegentScreen from './RegentScreen';
import ScheduleScreen from './ScheduleScreen';

/**
 * The gatherings of a choir, in time: a list, and one screen behind each row.
 *
 * Ported from `legacy/frontend/src/screens/SessionsScreen.tsx` at legacy commit 2953e3f.
 *
 * It was three tabs — «Спевка», «Отметиться» and «Расписание» — merged into one
 * on 2026-08-13 because they were never three subjects. This is the second
 * halving: the running gathering used to be a block above the list, the closed
 * ones a page of the «Хор» hub, and the programme and the sermon two more doors
 * off a card. All of them are one evening's own screen now, and the tab is the
 * list of evenings.
 *
 * What that closes is the seam the audit of 2026-08-22 named: one errand across
 * two tabs. The regent closed a gathering here and checked its roster there,
 * and the only thing joining the two was `reviewSession` in `App`.
 *
 * **What is here is T11, T12, T16 and T17 of `docs/specs/features/F-004/plan.md` §3** —
 * the list, the push, the swap that is this flow's boundary, and the greeting above them.
 * One thing was absent from it and is named at the line it belongs to: `SessionScreen`
 * itself, which §7 of the build spec gives, by mode, to F-006, F-012 and F-014. All three have
 * arrived since, as the paragraphs below say, and the placeholder that stood for them is gone.
 *
 * **T8 of `docs/specs/features/F-005/plan.md` §4 filled one third of that placeholder.**
 * `ahead` now leads to `AheadGathering`, because two of F-005's three acts live on that
 * screen and F-005 comes before F-006 in `docs/parity/ORDER.md:75-76` — §0.3 of the F-005
 * build spec. What stands there holds F-005's parts and nothing else, and its own header
 * names what it is missing and whose that is.
 *
 * **T6 of `docs/specs/features/F-006/plan.md` §4 put the `ahead` arm inside its own
 * `<Push at="evening" depth={0}>`**, as legacy returns every arm (`SessionScreen.tsx:152-160`,
 * `:215-224`), so the evening's DOM is as deep as the old app's.
 *
 * **T14 of `docs/specs/features/F-007/plan.md` §7 filled the second third**, and it is
 * `SessionScreen.tsx:178-201` as written: `RegentScreen` for a regent, the sentence for
 * `session == null`, `MarkScreen` for anybody else. The back key came with it —
 * `SessionScreen.tsx:150`, left to F-012 by F-006's build spec and moved here by `FND-148`
 * (`FIX`, 2026-09-12), because the arm that passes it `onBack` is this one.
 *
 * **T11 of `docs/specs/features/F-014/plan.md` §3 filled the last third**, and it is
 * `SessionScreen.tsx:203-213`: `PastGathering` inside its own `pushed(...)`, for either role, with
 * `isRegent` choosing its body. `OWNER` and the placeholder went with it.
 *
 * **T11 of `docs/specs/features/F-024/plan.md` §3 brought the programme over the evening** —
 * `program` with its doc comment (`SessionScreen.tsx:138-145`), legacy's own `pushed`
 * (`:152-160`), the `ProgramScreen` branch (`:162-173`) and the past arm's `onOpenProgram`
 * (`:210`). **T12** gave the ahead arm its `onOpenProgram` (`:222`).
 */

/**
 * Which of a gathering's three lives a row leads to.
 *
 * The old app declares this in `SessionScreen.tsx:57-60`, beside the screen that reads it.
 * That screen is not this flow's, so the type stands here — where the state that holds it
 * is — until the slice that ports `SessionScreen` takes it back.
 */
export type Open =
  | { mode: 'live' }
  | { mode: 'ahead'; item: ScheduledSession }
  | { mode: 'past'; item: ClosedSession };

/**
 * Where the evening's own screen stands, and where this flow stops.
 *
 * Its props are the old app's own (`SessionsScreen.tsx:111-126`) and it reads five of them:
 * `open`, `session`, and the three `AheadGathering` was given by T8 of the F-005 plan.
 * Everything the remaining slice will need is already handed over, so the swap that arrives
 * with it is a change to this file's imports and the arm itself. Legacy wraps every arm in
 * `<Push at='evening' depth={0}>` (`SessionScreen.tsx:156-160`); F-006 T6 brought that wrapper
 * for the `ahead` arm, F-007 T14 for the `live` one, and F-014 T11 for the `past` one.
 * This sentence said «and nothing else» until the F-005 slice review counted the wrapper.
 *
 * **`onClosed` is passed on as legacy passes it.** It is what legacy gives `RegentScreen`
 * (`:186`), and since F-013 the close calls it, as the cancel calls `onCancelled` since F-012.
 * `initialToken`, the attendance token a launch parameter carried in, was once carried through
 * here and never read, until F-008a; it now reaches `MarkScreen` as legacy hands it (`:195`).
 */
function SessionScreen({
  open,
  choir,
  session,
  initialToken,
  onBack,
  onChanged,
  onClosed,
}: {
  open: Open;
  choir: ChoirBrief;
  session: CurrentSession | null | undefined;
  initialToken: string | null;
  onBack: () => void;
  onChanged: () => void;
  onClosed: (sessionId: string) => void;
}): ReactNode {
  const isRegent = choir.role === 'regent';
  /**
   * The programme of this evening, when it is open.
   *
   * A screen and not a panel, the way it always was — it replaces this one
   * rather than growing inside it, because a list of numbers with its own
   * sign-up sheet is not a block.
   */
  const [program, setProgram] = useState(false);
  /**
   * The running service whose programme is open — `FND-1588`, a departure from the old app,
   * which draws no door on a running evening (`SessionScreen.tsx:162`). Taken at the tap rather
   * than read off `session` at each render, so a close that lands while the programme is open
   * does not throw the reader out of it.
   */
  const [liveProgram, setLiveProgram] = useState<string | null>(null);

  // A running gathering is drawn by this component; the other two modes are
  // components of their own and hold the key themselves. Null while one of them
  // is on screen, so this one does not hold a key nothing here draws.
  useBackKey(open.mode === 'live' ? onBack : null);

  /**
   * The evening, and its programme over it. Both branches, one `Push` - see
   * `SessionsScreen`, which is the same arrangement one level up.
   */
  const pushed = (screen: ReactNode): ReactNode => (
    <Push at={program ? 'program' : 'evening'} depth={program ? 1 : 0}>
      {screen}
    </Push>
  );

  const programId =
    open.mode === 'live'
      ? liveProgram
      : open.mode === 'ahead'
        ? open.item.session.session_id
        : open.item.session_id;
  if (program && programId !== null) {
    return pushed(
      <ProgramScreen
        sessionId={programId}
        onBack={() => {
          setProgram(false);
          onChanged();
        }}
      />,
    );
  }

  // The dispatch of `legacy SessionScreen.tsx:215-224`, its fall-through, drawn here ahead of the
  // `live` arm and so one indent deeper than legacy's `:222`. Legacy's `useBackKey` at `:150` is
  // `null` for this mode, and above it is written as legacy writes it.
  if (open.mode === 'ahead') {
    return pushed(
      <AheadGathering
        item={open.item}
        isRegent={isRegent}
        myMemberId={choir.member_id}
        onBack={onBack}
        onChanged={onChanged}
        onOpenProgram={() => setProgram(true)}
      />,
    );
  }

  // The gathering that is running draws its own head: a status line, the title,
  // and then the code or the check-in. Adding a second one here would put the
  // same evening's name on the screen twice.
  //
  // `legacy SessionScreen.tsx:178-201`, with `onClosed` of its `:186`, which the close calls
  // since F-013. `session == null` is loose, and it is ported as
  // written: `undefined` — the shell's poll has not answered yet — lands on the same sentence as
  // a gathering that really has ended, which is `FND-116` (`KEEP`) and the reason `MarkScreen`'s
  // own loading branch cannot be reached from here.
  if (open.mode === 'live') {
    return pushed(
      <>
        {isRegent ? (
          <RegentScreen
            myMark={session?.my_mark ?? null}
            onMarkedSelf={onChanged}
            onSessionsChanged={onChanged}
            onClosed={onClosed}
            onCancelled={onBack}
          />
        ) : session == null ? (
          <p className="hint">Спевка уже закончилась.</p>
        ) : (
          <MarkScreen
            session={session}
            choir={choir}
            initialToken={initialToken}
            onSessionMayHaveChanged={onChanged}
          />
        )}
        {/* `FND-1588`, `docs/specs/features/F-024/live-door.md`: the programme of the service
            being sung, as the ahead evening's door draws it (`AheadGathering.tsx`), without the
            count — `CurrentSession` carries none — and so always «Открыть программу». */}
        {session != null && session.kind === 'service' && (
          <div className="block">
            <span className="label">Программа</span>
            <button
              className="btn btn--quiet"
              onClick={() => {
                setLiveProgram(session.session_id);
                setProgram(true);
              }}
            >
              Открыть программу
              <ChevronRightIcon size={16} quiet />
            </button>
          </div>
        )}
      </>,
    );
  }

  if (open.mode === 'past') {
    return pushed(
      <PastGathering
        item={open.item}
        isRegent={isRegent}
        onBack={onBack}
        onChanged={onChanged}
        onOpenProgram={() => setProgram(true)}
      />,
    );
  }
}

export default function SessionsScreen({
  choir,
  home,
  session,
  initialToken,
  seenLive,
  onSeenLive,
  target = null,
  onTargetDone = () => {},
  onSessionChanged,
}: {
  choir: ChoirBrief;
  /** The lit tab, tapped again. */
  home: number;
  /** undefined — the server has not answered yet about a running gathering. */
  session: CurrentSession | null | undefined;
  initialToken: string | null;
  /**
   * The running gathering this launch has already put on screen — C-010. Held by the shell,
   * because this screen is rebuilt on every visit to the tab and would forget it.
   */
  seenLive: string | null;
  onSeenLive: (sessionId: string) => void;
  /**
   * The gathering the launch named — C-021, `FND-1616`. Held by the shell for `seenLive`'s
   * reason, and handed back through `onTargetDone` once it has been opened or not found.
   */
  target?: string | null;
  onTargetDone?: () => void;
  onSessionChanged: () => void;
}) {
  const isRegent = choir.role === 'regent';
  // Opening a gathering takes it out of the schedule, closing one puts it in
  // the history, and answering changes a count: either way the list behind was
  // loaded before it happened.
  const [reloadKey, setReloadKey] = useState(0);
  const reloadSchedule = useCallback(() => setReloadKey((key) => key + 1), []);
  /** Which evening is open, if one is. Null is the list. */
  const [open, setOpen] = useState<Open | null>(null);
  useHome(home, () => setOpen(null));

  /**
   * The running gathering opens itself for a singer who has not marked yet — C-010, `FND-1600`,
   * a departure from the old app, where it is one gold row of the list and the way to the code
   * and the scanner had to be found. Whoever leaves it by hand has seen it and is not sent
   * back on the same visit; the shell re-arms it on a return to the tab or to the app (§3.4). A
   * singer who could tap the gold row instead has seen it already, or has marked. A regent gets
   * the same, whatever their own mark, because the panel is where they show the choir the code —
   * C-010 §3.3, asked for on 2026-10-08.
   *
   * A launch from the QR the camera read lands here too, and the token is posted by the screen
   * this opens — before C-010 it waited for a tap on the row.
   */
  const unseen =
    target === null &&
    session != null &&
    (isRegent || session.my_mark === null) &&
    session.session_id !== seenLive
      ? session.session_id
      : null;
  // Over the list only: a singer reading another evening is not pulled out of it, and is
  // brought to the running one on the way back.
  const onList = open === null;
  /**
   * Whether the server has answered about a running gathering since this mounted. Read by
   * the opening below: the first answer of a launch is the one that opens the gathering
   * without a push — C-023 (`FND-1618`), the audit of 2026-10-27, where a launch into a
   * running rehearsal was a list fading in and sliding away at once, travel from a
   * screen nobody had seen. A later answer, and the re-arming on a return to the tab or
   * to the app, still push: there the person is looking at the list.
   */
  const answered = useRef(false);
  /** Whether the change of page under way is one no finger asked for. Cleared once drawn. */
  const quiet = useRef(false);
  useEffect(() => {
    if (unseen === null || !onList) return;
    onSeenLive(unseen);
    quiet.current = !answered.current;
    // oxlint-disable-next-line react/set-state-in-effect
    setOpen({ mode: 'live' });
  }, [unseen, onList, onSeenLive]);
  useEffect(() => {
    if (session !== undefined) answered.current = true;
  }, [session]);
  useEffect(() => {
    quiet.current = false;
  }, [open]);

  /**
   * The gathering the launch named opens itself — C-021, `FND-1616`, a departure from the old
   * app, whose reminder opened the list and left the person to find the evening it was about.
   * Asked for by Vitali on 2026-10-27: «после того как приходит уведомление … сразу открывалась
   * эта спевка там где отмечаться».
   *
   * It waits for the shell's poll, because the named evening may be running by the time the
   * button is tapped, and then it is the running one's screen that opens. Otherwise the row is
   * looked up in the same list request 1 asks for, and its screen is pushed over the list. A
   * gathering that is gone — closed, cancelled, of another choir — leaves the list, which is
   * what the button opened before. While it is pending C-010's opening by itself waits, so the
   * two do not fight over the screen; it is handed back either way, so it happens once.
   */
  // The id and not the object: the poll hands a new one every ten seconds, and each would
  // cancel the lookup in flight and send it again.
  const polled = session !== undefined;
  const runningId = session?.session_id ?? null;
  useEffect(() => {
    if (target === null || !polled) return;
    if (runningId === target) {
      onTargetDone();
      onSeenLive(target);
      // oxlint-disable-next-line react/set-state-in-effect
      setOpen({ mode: 'live' });
      return;
    }
    let current = true;
    void (async () => {
      let row: ScheduledSession | undefined;
      try {
        const rows = isRegent ? await api.schedule() : await api.upcoming();
        row = rows.find((one) => one.session.session_id === target);
      } catch {
        // The list draws its own refusal; there is nothing to open.
      }
      if (!current) return;
      onTargetDone();
      if (row !== undefined) setOpen({ mode: 'ahead', item: row });
    })();
    return () => {
      current = false;
    };
  }, [target, polled, runningId, isRegent, onTargetDone, onSeenLive]);

  // On screen counts as seen, however it got there. The shell re-arms on a return to the app
  // (§3.4), and an evening already open must not take that as a reason to come back the
  // moment it is left.
  const liveOpen = open?.mode === 'live' && session != null ? session.session_id : null;
  useEffect(() => {
    if (liveOpen !== null) onSeenLive(liveOpen);
  }, [liveOpen, onSeenLive, seenLive]);

  /**
   * A gathering has just been closed, and the screen stays on it.
   *
   * The summary and the roster of the same evening one under the other is what
   * this whole step is for, so closing does not go back to the list: it asks
   * the history for the row that has just appeared and shows it. By id and not
   * by page - the history is paginated, and a handful of older rehearsals
   * still awaiting review sort ahead of this one and could push it past the
   * first page. If that request fails there is nothing to show and the list is
   * the honest fallback — the gathering is closed either way, and it is in the
   * list's tail.
   *
   * `api.history({ sessionId })` is the old app's `api.sessionHistoryById` — the rebuild's
   * client has one method for `/session/history` and the query decides which page it means
   * (`api.ts:245`). The request is the same request.
   *
   * The close calls this since F-013: `RegentScreen`'s `closeSession` calls the `onClosed`
   * that `SessionScreen` above passes on. It was ported before that because it is this file's,
   * and §7 of the build spec says so — «F-004 owns only what happens after: refetch by id and
   * show it».
   */
  const closed = useCallback(
    async (sessionId: string) => {
      onSessionChanged();
      reloadSchedule();
      try {
        const page = await api.history({ sessionId });
        const item = page.sessions[0];
        setOpen(item === undefined ? null : { mode: 'past', item });
      } catch {
        setOpen(null);
      }
    },
    [onSessionChanged, reloadSchedule],
  );

  /**
   * The gathering ended under the screen that was showing it.
   *
   * A rehearsal closes itself two hours after it was opened, and the regent may
   * be looking at the panel when it does — `RegentScreen`'s own poll then has
   * no code to draw and the screen would be a back row over nothing. Closing it
   * by hand is not this: that hands the screen over to the same evening's
   * summary, and by then `open` is `past`.
   */
  useEffect(() => {
    // `null` and not a falsy check: `undefined` is «the poll has not answered», and a
    // screen closed by that would be closed by every reconnection — AC-29.
    // oxlint-disable-next-line react/set-state-in-effect
    if (open?.mode === 'live' && session === null) setOpen(null);
  }, [open, session]);

  /**
   * The list, and one evening's own screen over it.
   *
   * Both branches below are wrapped rather than one, because what does the
   * animating is the same `Push` in the same place in the tree either way - it
   * is the one that keeps the screen being left mounted while it goes, and a
   * second element in a second branch would be a second component with nothing
   * held in it.
   *
   * Two depths and no finer identity on purpose: closing a rehearsal hands the
   * same screen from the panel to that evening's summary - `live` becomes
   * `past` under the regent's own hand - and that is one screen changing rather
   * than another arriving over it.
   */
  const pushed = (screen: ReactNode): ReactNode => (
    <Push
      at={open === null ? 'list' : 'evening'}
      depth={open === null ? 0 : 1}
      motion={!quiet.current}
    >
      {screen}
    </Push>
  );

  if (open !== null) {
    return pushed(
      <SessionScreen
        open={open}
        choir={choir}
        session={session}
        initialToken={initialToken}
        onBack={() => {
          setOpen(null);
          reloadSchedule();
          onSessionChanged();
        }}
        onChanged={() => {
          onSessionChanged();
          reloadSchedule();
        }}
        onClosed={(sessionId) => void closed(sessionId)}
      />,
    );
  }

  return pushed(
    <>
      {/* Above everything, and only on the day: it is a greeting, and a
          greeting under a screenful of schedule is a greeting nobody reads. */}
      <BirthdayCard />

      <h1 className="screen-title">Расписание</h1>

      {/* `onSessionChanged` is the old app's sixth prop here (`:143`), and T21 brings it:
          its two callers are `act` and `add` (`legacy ScheduleScreen.tsx:326`, `:365`).
          **T16's note here said `act` was F-025's, and that was wrong** — `act` is the
          list's own helper for anything that changes a gathering, and «Не смогу» is the
          first thing to use it. `add` really is F-005's. */}
      <ScheduleScreen
        isRegent={isRegent}
        session={session}
        reloadKey={reloadKey}
        onOpen={setOpen}
        onSessionChanged={onSessionChanged}
      />
    </>,
  );
}
